<script setup>
const { locale, locales, setLocale, t } = useI18n();
const route = useRoute();

const open = ref(false);
const root = ref(null);

function onClickOutside(e) {
  if (!root.value) return;
  if (!root.value.contains(e.target)) open.value = false;
}
onMounted(() => document.addEventListener("click", onClickOutside));
onBeforeUnmount(() => document.removeEventListener("click", onClickOutside));

const current = computed(() =>
  locales.value.find((l) => l.code === locale.value)
);

async function pick(code) {
  open.value = false;
  await setLocale(code);
}
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      :aria-label="t('lang.switch')"
      :title="current?.name"
      :aria-expanded="open"
      class="grid h-10 w-10 place-items-center rounded-full border border-line/80 text-ink hover:border-brand-green hover:text-brand-green transition"
      @click.stop="open = !open"
    >
      <svg aria-hidden="true" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18 15 15 0 010-18z" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute right-0 mt-2 min-w-[10rem] rounded-2xl border border-line bg-paper p-1 shadow-soft z-50"
      >
        <button
          v-for="l in locales"
          :key="l.code"
          type="button"
          :aria-current="l.code === locale ? 'true' : undefined"
          class="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-soft"
          :class="l.code === locale ? 'bg-soft font-medium text-brand-green' : 'text-ink'"
          @click="pick(l.code)"
        >
          <span>{{ l.name }}</span>
          <svg
            v-if="l.code === locale"
            aria-hidden="true"
            class="h-4 w-4 shrink-0"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
          >
            <path d="M4 10.5l4 4 8-9" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </Transition>
  </div>
</template>
